import { useTranslation } from "react-i18next";
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, PlayCircleIcon, ClockIcon, TagIcon } from 'lucide-react';
interface TutorialCardProps {
  id: number;
  title: string;
  description: string;
  category: string;
  duration: string;
  completed: boolean;
  completedAt: string | null;
  icon: React.ReactNode;
}
export function TutorialCard({
  id,
  title,
  description,
  category,
  duration,
  completed,
  completedAt,
  icon
}: TutorialCardProps) {
  const {
    t
  } = useTranslation("tutorials");
  const navigate = useNavigate();
  // Format the completion date if available
  const formatCompletionDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };
  const completionDate = formatCompletionDate(completedAt);
  const handleCardClick = () => {
    navigate(`/tutorial/${id}`);
  };
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when button is clicked
    navigate(`/tutorial/${id}`);
  };
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm flex flex-col h-full cursor-pointer" onClick={handleCardClick} data-id="element-15046" data-mp="32zt1y5zd4ljt">
      
      <div className="p-5 flex-grow" data-id="element-15047" data-mp="37xuudhiohx7y">
        <div className="flex justify-between items-start mb-3" data-id="element-15048" data-mp="18ls51qndi8md">
          <div className="p-2 rounded-lg bg-gray-50" data-id="element-15049" data-mp="18nmpz4yna0qg">{icon}</div>
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${completed ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} data-id="element-15050" data-mp="1dlo7hlgf9rzt">
            
            {completed ? 'Completed' : 'Not completed'}
          </div>
        </div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-15051" data-mp="2pxs3y6ztxsv5">{title}</h3>
        <p className="text-sm text-gray-500 mb-4 line-clamp-2" data-id="element-15052" data-mp="2kw2psihb8z2u">{description}</p>
        <div className="flex items-center justify-between mt-auto" data-id="element-15053" data-mp="2ztv3d53ardee">
          <div className="flex items-center text-xs text-gray-500" data-id="element-15054" data-mp="2zs1ynvc8v391">
            <TagIcon size={14} className="mr-1" data-id="element-15055" data-mp="2uusa0hwoivm9" />
            {category}
          </div>
          <div className="flex items-center text-xs text-gray-500" data-id="element-15056" data-mp="38lezdjgjv95o">
            <ClockIcon size={14} className="mr-1" data-id="element-15057" data-mp="34klbgq33xucs" />
            {duration}
          </div>
        </div>
      </div>
      <div className={`px-5 py-3 border-t border-gray-100 flex justify-between items-center ${completed ? 'bg-green-50/50' : 'bg-gray-50/50'}`} data-id="element-15058" data-mp="3ik3117o89bn7">
        
        <button onClick={handleButtonClick} className={`flex items-center text-sm font-medium transition-colors ${completed ? 'text-gray-600 hover:text-gray-700' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-15059" data-mp="3djymhn6vqqzm">
          
          {completed ? <>
              <CheckCircleIcon size={16} className="mr-1.5" data-id="element-15060" data-mp="3n8dnp5k41ng6" />
              {t("components.TutorialCard.revisit_tutorial")}
            </> : <>
              <PlayCircleIcon size={16} className="mr-1.5" data-id="element-15061" data-mp="3sal2e2wbrhm7" />
              {t("components.TutorialCard.start_tutorial")}
            </>}
        </button>
        {completed && completionDate && <span className="text-xs text-gray-500" data-id="element-15062" data-mp="3j94uc1szfrl5">
            {t("components.TutorialCard.completed")} {completionDate}
          </span>}
      </div>
    </div>;
}